import {
  ModuleRegistry,
  ReactLazyComponent,
  ReactLazyComponentOptions,
} from 'react-module-container';
import { WIDGET_BUNDLE_FILE_NAME } from '../constants';
import { InteractionName } from '../types/InteractionName';
import { UILibrary } from '../types/UILibrary';
import { InternalPaymentsWidgetProps } from '../types/InternalPaymentsWidgetProps';
import { fedopsLogger } from '../utils/fedopsLogger';
import {
  getLoadByInteraction,
  tpasToTrackLoadBy,
} from '../utils/fedopsLogger/getLoadByInteraction';
import { interactionEndedCrossEnv } from '../utils/fedopsLogger/interactionEndedCrossEnv';
import { perfNow } from '../utils/perfNow';
import { IWidgetLazyComponentProps, LAZY_WIDGET_ID, WIDGET_ID } from './config';
import { hasDebugParam } from './hasDebugParam';

export class WidgetLazyComponent extends ReactLazyComponent {
  static buildFiles(props: IWidgetLazyComponentProps): string[] {
    const isDebug: boolean = props.debug || hasDebugParam();
    const baseUrl = props.widgetSettings.widgetStaticsBaseUrl;
    const uiLibrary: UILibrary = props.configuration.isMerchantContext
      ? 'wsr'
      : 'tpa';

    return [
      `${baseUrl}${WIDGET_BUNDLE_FILE_NAME}.${uiLibrary}${
        isDebug ? '' : '.min'
      }.css`,
      `${baseUrl}${WIDGET_BUNDLE_FILE_NAME}.${uiLibrary}.bundle${
        isDebug ? '' : '.min'
      }.js`,
    ];
  }

  constructor(props: IWidgetLazyComponentProps) {
    const isLoggerPassedThrough: boolean = !!props.fedopsLogger;
    // avoid bundling fedopsLogger once all TPAs will GA their bundles with latest lazy-public that passed logger as a prop
    const logger = isLoggerPassedThrough ? props.fedopsLogger : fedopsLogger;

    super(props, {
      component: WIDGET_ID,
      files: WidgetLazyComponent.buildFiles(props),
      crossorigin: true,
      resolve: (): Promise<Partial<InternalPaymentsWidgetProps>> =>
        Promise.resolve({
          startLoadTime: undefined, // filtering out, passed as a part of fedopsMeta
          fedopsLogger: logger,
          fedopsMeta: {
            startLoadTime: props.startLoadTime,
            isLoadInteractionStarted: true,
            isLoggerPassedThrough,
          },
        }),
    } as ReactLazyComponentOptions);

    if (isLoggerPassedThrough) {
      logger.interactionEnded(InteractionName.LAZY_COMPONENT_LOAD);
    } else {
      interactionEndedCrossEnv<InteractionName>({
        logger,
        interactionName: InteractionName.LAZY_COMPONENT_LOAD,
        startTime: props.startLoadTime,
        endTime: perfNow(),
      });
    }

    logger.interactionStarted(InteractionName.WIDGET_LOAD);

    if (tpasToTrackLoadBy.indexOf(props.configuration?.appId) !== -1) {
      logger.interactionStarted(
        getLoadByInteraction(props.configuration?.appId)
      );
    }
  }
}

ModuleRegistry.registerComponent(LAZY_WIDGET_ID, () => WidgetLazyComponent);
